import { ReactNode, useState } from 'react';
import { Switch, Text, InfoLabel } from '@fluentui/react-components';

import './ConceptForm.Styles.css';

/**
 * Ids for the label/switch pairing. A plain counter rather than a hook from the
 * design system: React 17 has no useId, and depending on one here would break
 * under the hand-written Fluent mock the form's tests use.
 */
let toggleIdSequence = 0;

interface FormSectionProps {
  title: string;
  description?: string;
  icon?: JSX.Element;
  children: ReactNode;
}

/**
 * A titled block of related fields. The form is long enough that bare
 * separators gave no sense of where one topic ends and the next begins.
 */
export const FormSection = (props: FormSectionProps) => (
  <section className='ConceptFormSection'>
    <header className='ConceptFormSectionHeader'>
      {props.icon && <span className='ConceptFormSectionIcon'>{props.icon}</span>}
      <div className='ConceptFormSectionHeading'>
        <h3 className='ConceptFormSectionTitle'>{props.title}</h3>
        {props.description && (
          <Text className='ConceptFormSectionDescription'>{props.description}</Text>
        )}
      </div>
    </header>
    <div className='ConceptFormSectionBody'>{props.children}</div>
  </section>
);

interface ToggleFieldProps {
  label: string;
  /** Only pass one when it adds something the label does not already say. */
  description?: string;
  info?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Dependent fields, revealed only while the toggle is on. */
  children?: ReactNode;
}

/**
 * Label and description on the left, switch flushed right.
 *
 * Down a list of thirty toggles this is what makes the state readable: every
 * label starts at the same x and every switch ends at the same x, so the on/off
 * pattern can be scanned as a column instead of hunted for line by line.
 */
export const ToggleField = (props: ToggleFieldProps) => {
  const [switchId] = useState(() => 'conceptFormToggle-' + (++toggleIdSequence));

  return (
    <div className={props.checked ? 'ConceptFormToggle Active' : 'ConceptFormToggle'}>
      <div className='ConceptFormToggleRow'>
        {/* a real <label> keeps the text clickable even though the switch is detached from it */}
        <label className='ConceptFormToggleText' htmlFor={switchId}>
          <span className='ConceptFormToggleLabel'>
            {props.label}
            {props.info && <InfoLabel className='ConceptFormToggleInfo' info={props.info} />}
          </span>
          {props.description && (
            <Text className='ConceptFormToggleDescription'>{props.description}</Text>
          )}
        </label>
        <Switch
          id={switchId}
          className='ConceptFormToggleSwitch'
          checked={props.checked}
          onChange={(ev, data) => props.onChange(data.checked)}
        />
      </div>
      {props.checked && props.children && (
        <div className='ConceptFormNested'>{props.children}</div>
      )}
    </div>
  );
};

/** Indented block for fields that depend on a choice made just above them. */
export const NestedFields = (props: { children: ReactNode }) => (
  <div className='ConceptFormNested'>{props.children}</div>
);
